import * as React from "react"
import Link from "next/link"
import { ShieldAlert } from "lucide-react"
import { ArrowRightIcon } from "@/components/brand-kit/icon-sprite"
import { SectionHeader } from "./section-header"
import type { BrandRecall } from "./types"

/**
 * The brand recall record — FRIDGE-401 Step 1.
 *
 * The card's finding: regional dealers beat us with `-overview` brand pages,
 * and the copyable counter is data a dealer cannot match. This section puts
 * the brand's official CPSC recall history ON the brand page, linking each
 * notice to its /recalls/[slug] record page.
 *
 * Real-or-Hidden: renders NOTHING when the brand has no curated notice —
 * never an empty state, never a reassuring "no recalls!" claim (the curated
 * record is refrigeration-scoped, so absence is not a safety guarantee and
 * must not be presented as one).
 */

function formatUnits(count: number | null): string | null {
  if (!count || count <= 0) return null
  return `${count.toLocaleString("en-US")} units`
}

function formatDate(iso: string): string {
  // recall_date is a plain date column (YYYY-MM-DD); render MM/DD/YYYY
  // without letting the viewer's timezone shift the day.
  const [y, m, d] = iso.slice(0, 10).split("-")
  if (!y || !m || !d) return iso
  return `${m}/${d}/${y}`
}

export function RecallRecord({
  brandName,
  recalls,
}: {
  brandName: string
  recalls: BrandRecall[]
}) {
  if (!recalls || recalls.length === 0) return null

  return (
    <section id="brand-recall-record" className="my-12 scroll-mt-[110px]">
      <SectionHeader
        eyebrow="Safety history"
        title={`The ${brandName} Recall Record`}
        description={`Official U.S. Consumer Product Safety Commission refrigeration notices naming ${brandName} as the recalling brand, tracked at Fridge.com with links to every source notice.`}
        action={{ type: "link", label: "Full recall record", href: "/recalls" }}
      />
      <ul className="grid gap-3 sm:grid-cols-2">
        {recalls.map((r) => (
          <li key={r.recall_number}>
            <Link
              href={`/recalls/${r.slug}`}
              className="group flex h-full flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-5 transition-colors hover:border-[#0469f0]/40 hover:bg-blue-50/40"
            >
              <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide text-gray-500">
                <ShieldAlert className="h-4 w-4 text-[#ff9951]" aria-hidden />
                <span>CPSC {r.recall_number}</span>
                <span aria-hidden>·</span>
                <span>{formatDate(r.recall_date)}</span>
              </div>
              <p className="text-[15px] font-bold leading-snug text-gray-900">
                {r.title}
              </p>
              {r.hazard_text && (
                <p className="line-clamp-2 text-[13px] leading-relaxed text-gray-600">
                  {r.hazard_text}
                </p>
              )}
              <div className="mt-auto flex items-center justify-between pt-1">
                <span className="text-[12px] font-semibold text-gray-500">
                  {[r.product_category_label, formatUnits(r.units_affected_count)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span className="inline-flex items-center gap-1 text-[13px] font-bold text-[#0469f0]">
                  What to check
                  <ArrowRightIcon
                    className="h-3 w-3 transition-transform duration-150 group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
